import { PageBodyProps } from '@pypestream/design-system';
import { useParams } from 'react-router-dom';

import { useProStudioCtxSelector } from '../xstate/app.xstate';
import {
  Home,
  Versions,
  Builder,
  Channels,
  Actions,
  Files,
  ChannelSettings,
  ChannelCreate,
  ProjectConfigurations,
  VoiceChannelSettings,
} from '../pages';

export enum ProStudioPages {
  HOME = 'Home',
  CONFIGURATIONS = 'Configurations',
  VERSIONS = 'Versions',
  WORKFLOWS = 'Workflows',
  CHANNELS = 'Channels',
  ACTIONS = 'Actions',
  FILES = 'Files',
  CHANNEL_SETTINGS = 'Channel settings',
  CHANNEL_CREATION = 'Channel creation',
  VOICE_CHANNEL_CREATION = 'Voice Channel Creation',
  VOICE_CHANNEL_SETTINGS = 'Voice Channel Settings',
}

export interface StudioPageProps {
  pageBodyProps?: PageBodyProps;
  loading?: boolean;
  label?: string;
  setLoading?: (loading: boolean) => void;
}

export const useProStudioRoutes = () => {
  const {
    botId: botIdParam,
    channelId: channelIdParam,
    projectId: projectIdParam,
  } = useParams();

  const { botIdCtx, channelIdCtx, projectIdCtx } = useProStudioCtxSelector(
    ({ botId, channelId, project }) => ({
      botIdCtx: botId,
      channelIdCtx: channelId,
      projectIdCtx: project?.id,
    })
  );

  const botId = botIdParam || botIdCtx;
  const channelId = channelIdParam || channelIdCtx;
  const projectId = projectIdParam || projectIdCtx;

  const botBasePath = projectId && botId ? `/${projectId}/${botId}` : undefined;

  const commonPageBodyProps: PageBodyProps = {
    width: 'xlarge',
    background: 'none',
  };

  return {
    [ProStudioPages.HOME.toLowerCase()]: {
      path: projectId ? `/${projectId}` : '/',
      label: 'Solutions',
      schema: ':projectId',
      element: <Home pageBodyProps={{ background: 'none' }} />,
    },
    [ProStudioPages.CONFIGURATIONS.toLowerCase()]: {
      path: `/${projectId}/configurations`,
      label: ProStudioPages.CONFIGURATIONS,
      schema: ':projectId/configurations',
      element: <ProjectConfigurations pageBodyProps={{ background: 'none' }} />,
    },
    [ProStudioPages.VERSIONS.toLowerCase()]: {
      path: botBasePath || '',
      label: ProStudioPages.VERSIONS,
      schema: ':projectId/:botId',
      element: <Versions pageBodyProps={commonPageBodyProps} />,
    },
    [ProStudioPages.WORKFLOWS.toLowerCase()]: {
      path: botBasePath ? `${botBasePath}/workflows` : '',
      label: ProStudioPages.WORKFLOWS,
      schema: ':projectId/:botId/workflows',
      element: (
        <Builder
          pageBodyProps={{
            ...commonPageBodyProps,
            spacing: 'none',
            background: 'none',
          }}
        />
      ),
    },
    [ProStudioPages.CHANNELS.toLowerCase()]: {
      path: botBasePath ? `${botBasePath}/channels` : '',
      label: ProStudioPages.CHANNELS,
      schema: ':projectId/:botId/channels',
      element: <Channels pageBodyProps={commonPageBodyProps} />,
    },
    [ProStudioPages.CHANNEL_SETTINGS.toLowerCase().replace(/\s+/g, '_')]: {
      path: botBasePath ? `${botBasePath}/channels/${channelId}` : '',
      label: ProStudioPages.CHANNEL_SETTINGS,
      schema: ':projectId/:botId/channels/:channelId',
      element: <ChannelSettings pageBodyProps={{ background: 'none' }} />,
    },
    [ProStudioPages.CHANNEL_CREATION.toLowerCase().replace(/\s+/g, '_')]: {
      path: botBasePath ? `${botBasePath}/channels/create` : '',
      label: ProStudioPages.CHANNEL_CREATION,
      schema: ':projectId/:botId/channels/create',
      element: <ChannelCreate pageBodyProps={{ background: 'none' }} />,
    },
    [ProStudioPages.ACTIONS.toLowerCase()]: {
      path: botBasePath ? `${botBasePath}/actions` : '',
      label: ProStudioPages.ACTIONS,
      schema: ':projectId/:botId/actions',
      element: <Actions pageBodyProps={commonPageBodyProps} />,
    },
    [ProStudioPages.FILES.toLowerCase()]: {
      path: botBasePath ? `${botBasePath}/files` : '',
      label: ProStudioPages.FILES,
      schema: ':projectId/:botId/files',
      element: <Files pageBodyProps={commonPageBodyProps} />,
    },
    [ProStudioPages.VOICE_CHANNEL_CREATION.toLowerCase().replace(/\s+/g, '_')]:
      {
        path: botBasePath ? `${botBasePath}/voice-channel/create` : '',
        label: ProStudioPages.VOICE_CHANNEL_CREATION,
        schema: ':projectId/:botId/voice-channel/create',
        element: (
          <VoiceChannelSettings pageBodyProps={{ background: 'none' }} />
        ),
      },
    [ProStudioPages.VOICE_CHANNEL_SETTINGS.toLowerCase().replace(/\s+/g, '_')]:
      {
        path: botBasePath ? `${botBasePath}/voice-channel/:channelId` : '',
        label: ProStudioPages.VOICE_CHANNEL_SETTINGS,
        schema: ':projectId/:botId/voice-channel/:channelId',
        element: (
          <VoiceChannelSettings pageBodyProps={{ background: 'none' }} />
        ),
      },
  };
};
